import { DndContext, closestCenter } from '@dnd-kit/core'
import { SortableContext } from '@dnd-kit/sortable'
import { restrictToParentElement } from '@dnd-kit/modifiers'

import { Box, Button, Stack } from '@mui/material'
import { PlusIcon } from 'shared/icons'
import Grid from '@mui/material/Grid'

import { Loading } from 'components/controls/loading'
import { BlockCaption } from 'components/controls/blockCaption'

import { useListSearchsConvention, useRemoveSearchsConvention, useSortSearchsConvention } from 'interface/search/convention'

import { useSortableList } from '../container/useSortableList'
import { useSortableListEdit } from '../container/useSortableListEdit'
import { EdittableDataListRecord, StaticDataListRecord } from '../components/record'

import { EditSearchsConventionDialog } from './dialog/edit'
import { AddSearchsConventionDialog } from './dialog/add'
import { ErrorInfo } from 'components/controls/errorInfo'

const SearchsConventionCaption = "対象学会"

const gridRules = {
  size: 12
}

type EdittableSearchsConventionListBlockProps = {
  searchId: number
  openConvention: (conventionId: number) => void
}
const EdittableSearchsConventionListBlock = ({
  searchId,
  //openConvention
}: EdittableSearchsConventionListBlockProps) => {
  const { loading, data, error } = useListSearchsConvention(searchId)
  const list = useSortableList({ list: data?.list ?? [] })
  const { sortableList } = list
  const [postState, post] = useSortSearchsConvention(searchId)
  const [removeState, remove] = useRemoveSearchsConvention(searchId)
  const {
		modal, closeModal,
		onAdd, onEditIt, onRemoveIt,
		onSubmitEdit, onSubmitAdd,
		onDragEnd
  } = useSortableListEdit({
    ...list,
    post: (data) => post(data?.map(r => r.searchsConventionId) ?? [])
  })
  return (
    <Stack direction="column" position="relative">
      <Stack direction="row" spacing={1} alignItems="flex-end" width="100%" >
        <Box flexGrow={1}><BlockCaption flexGrow={1} caption={SearchsConventionCaption}/></Box>
        <Button
          startIcon={<PlusIcon />}
          variant="contained"
          size="small"
          sx={{ width: 'auto' }}
          onClick={onAdd}
        >
          追加
        </Button>
      </Stack>
      {error && <ErrorInfo error={error}/>}
      {(loading || postState.posting || removeState.posting) && <Loading/>}
      {(postState.posting || removeState.posting) && <Loading />}

      {sortableList.length > 0 && (
        <Grid container flexGrow={1} spacing={1} marginTop={1}>
          <DndContext
            collisionDetection={closestCenter}
            modifiers={[restrictToParentElement]}
            onDragEnd={onDragEnd}
          >
            <SortableContext items={sortableList}>
              {sortableList.map((convention, i) => (
                <EdittableDataListRecord
                  key={`convention-${i}`}
                  data={convention}
                  displayName={convention.conventionName ?? 'No name'}
                  onEditIt={() => {
                    onEditIt(convention)
                  }}
                  onRemoveIt={() => {
                    remove(convention.searchsConventionId)
                    onRemoveIt(convention)
                  }}
                  {...gridRules}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Grid>
      )}

      { modal?.command === 'add' ? (
        <AddSearchsConventionDialog
          searchId={searchId}
          onSubmit={onSubmitAdd}
          onClose={closeModal}
        />
      ) : (modal?.command === 'edit' && modal.data) ? (
        <EditSearchsConventionDialog
          searchId={searchId}
          convention={modal.data}
          onSubmit={onSubmitEdit}
          onClose={closeModal}
        />
      ) : (
        <></>
      )}
    </Stack>
  )
}

type StaticSearchsConventionListBlockProps = {
  searchId: number
  openConvention: (conventionId: number) => void
}
const StaticSearchsConventionListBlock = ({
  searchId,
  //openConvention
}: StaticSearchsConventionListBlockProps) => {
  const { loading, data, error } = useListSearchsConvention(searchId)
  if (loading || !data || data.list.length === 0) return <></>
  return (
    <Stack direction="column" position="relative">
      <BlockCaption caption={SearchsConventionCaption} />
      {error && <ErrorInfo error={error}/>}

      <Grid container flexGrow={1} spacing={1} marginTop={0.5}>
        {data.list.map((convention, i) => (
          <StaticDataListRecord
            key={`convention-${i}`}
            displayName={convention.conventionName ?? 'No name'}
            {...gridRules}
          />
        ))}
      </Grid>
    </Stack>
  )
}

type SearchsConventionListBlockProps = {
  edittable: boolean
  searchId: number
  openConvention: (conventionId: number) => void
}
export const SearchsConventionListBlock = ({
  edittable,
  searchId,
  openConvention
}: SearchsConventionListBlockProps) => edittable ? (
  <EdittableSearchsConventionListBlock
    searchId={searchId}
    openConvention={openConvention}
  />
) : (
  <StaticSearchsConventionListBlock
    searchId={searchId}
    openConvention={openConvention}
  />
)
